/**
 * 申請フォームの中身(新しい申請ウィザードの各段)。6 種別すべて同じ入口(kind で出し入れを変える)。
 *
 * 旧 Modal の入力欄をそのまま 3 つに割った: 対象者(RequestTarget)/ 内容(RequestContent)/
 * 確認(RequestConfirm)。入力欄・検証・API は変えていない(状態は requestDraft.ts)。
 * 入社申請の対象者段だけは、登録済みの人を選ぶ代わりに従業員を新規入力する(RequestNewEmployee。
 * 状態は newEmployee.ts)。
 * E2E の目印 `data-field` / `data-return-loan` と placeholder の文言も旧 Modal のまま。
 */
import {
  Alert,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Tooltip,
  TreeSelect,
  Typography,
} from "antd";
import dayjs from "dayjs";

import { KeyValueList, type KvItem } from "../../ui";
import { APPROVER_KINDS, EMPLOYMENT_TYPES, FIELD_TYPES, STATUS_FOR_REQUEST, kindLabel, statusLabel } from "./constants";
import { ManagerSelect } from "./EmployeeForm";
import { errorText, positionOptions, unitOptions, useOrgUnits, usePositions } from "./hooks";
import type { NewEmployeeState } from "./newEmployee";
import { fieldKey, itemTag, replaceWithin, targetItems, type RequestDraft } from "./requestDraft";
import { SimilarWarning } from "./SimilarWarning";

/** 対象者の段。候補は種別ごとの在籍状態と検索語でサーバーが絞る。 */
export function RequestTarget({ draft }: { draft: RequestDraft }) {
  const { kind, candidates, employees, term, setSearch, empId, setEmpId } = draft;
  return (
    <div>
      <Typography.Text strong>対象者</Typography.Text>
      <Select
        style={{ width: "100%", marginTop: 4 }}
        placeholder={`${kindLabel(kind)}申請を出せる人だけ出る`}
        showSearch
        // サーバーで絞ったものをそのまま出す(手元の 200 件だけで絞らない)
        filterOption={false}
        onSearch={setSearch}
        value={empId}
        loading={employees.isFetching}
        onChange={(v: number) => setEmpId(v)}
        options={candidates.map((e) => ({
          value: e.id,
          label: `${e.name}(${e.emp_code} / ${statusLabel(e.status)})`,
        }))}
      />
      {candidates.length === 0 && !employees.isFetching && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {term
            ? `「${term}」に当てはまる対象者がいない`
            : `対象になる在籍状態の人がいない(必要な状態: ${(STATUS_FOR_REQUEST[kind] ?? [])
                .map(statusLabel)
                .join(" / ")})`}
        </Typography.Text>
      )}
    </div>
  );
}

/** 入社申請の対象者の段。登録済みの人は選ばず、ここで入力した人を申請と一緒に従業員として登録する
 * (POST /api/hr/onboarding)。項目は従業員の登録フォームの新規登録と同じ。在籍状態は入社前に固定、
 * 入社日は「内容」段の入社日と兼ねるので、どちらもここには出さない。
 *
 * 入力欄の id は E2E から getByLabel で引けるよう htmlFor と揃える。 */
export function RequestNewEmployee({ emp }: { emp: NewEmployeeState }) {
  const { value: v, set, setExtra, fields } = emp;
  // 所属・役職の選択肢は有効なものだけ(従業員の登録フォームと同じ)
  const units = useOrgUnits(true);
  const positions = usePositions(true);
  const id = (k: string) => `new-employee-${k}`;
  return (
    <Form layout="vertical" component="div">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="入社する人をここで入力する。申請を出すと従業員としても登録される(在籍状態は「入社前」)"
        description="どちらかが失敗したら両方とも作らない。入社日は次の「内容」で入れる(従業員の入社日にも同じ日が入る)。"
      />
      <Form.Item label="社員番号" htmlFor={id("emp_code")} extra="空のままなら契約形態から自動で採番する(正社員 AM / その他 OT / それ以外 BP + 次の番号)">
        <Input id={id("emp_code")} maxLength={40} placeholder="空なら自動採番" value={v.emp_code} onChange={(e) => set({ emp_code: e.target.value })} />
      </Form.Item>
      {/* 氏名は姓・名で入れる。表示名(`name`)はサーバーが導出するので入力欄は無い */}
      <Space wrap size="middle" align="start">
        <Form.Item label="姓" htmlFor={id("family_name")} required>
          <Input id={id("family_name")} maxLength={100} placeholder="山田" value={v.family_name} onChange={(e) => set({ family_name: e.target.value })} />
        </Form.Item>
        <Form.Item label="名" htmlFor={id("given_name")} required>
          <Input id={id("given_name")} maxLength={100} placeholder="太郎" value={v.given_name} onChange={(e) => set({ given_name: e.target.value })} />
        </Form.Item>
        <Form.Item label="姓カナ" htmlFor={id("family_name_kana")}>
          <Input
            id={id("family_name_kana")}
            maxLength={100}
            placeholder="ヤマダ"
            value={v.family_name_kana}
            onChange={(e) => set({ family_name_kana: e.target.value })}
          />
        </Form.Item>
        <Form.Item label="名カナ" htmlFor={id("given_name_kana")}>
          <Input
            id={id("given_name_kana")}
            maxLength={100}
            placeholder="タロウ"
            value={v.given_name_kana}
            onChange={(e) => set({ given_name_kana: e.target.value })}
          />
        </Form.Item>
      </Space>
      <Form.Item
        label="メールアドレス"
        htmlFor={id("email")}
        extra="GWS のアカウント発行を選ぶ場合は必須。重複は大小文字を無視して弾く"
      >
        <Input id={id("email")} maxLength={200} placeholder="taro.yamada@example.com" value={v.email} onChange={(e) => set({ email: e.target.value })} />
      </Form.Item>
      <SimilarWarning familyName={v.family_name} givenName={v.given_name} email={v.email} />
      <Space wrap size="middle" align="start">
        <Form.Item label="契約形態" htmlFor={id("employment_type")} required>
          <Select
            id={id("employment_type")}
            style={{ width: 200 }}
            value={v.employment_type}
            onChange={(x: string) => set({ employment_type: x })}
            options={EMPLOYMENT_TYPES.map((t) => ({ value: t, label: t }))}
          />
        </Form.Item>
        <Form.Item label="所属(主所属)" htmlFor={id("org_unit_id")}>
          <TreeSelect
            id={id("org_unit_id")}
            style={{ width: 260 }}
            allowClear
            treeDefaultExpandAll
            treeData={unitOptions(units.data?.items ?? [])}
            placeholder="未所属"
            value={v.org_unit_id}
            onChange={(x: number | undefined) => set({ org_unit_id: x })}
          />
        </Form.Item>
        <Form.Item label="役職" htmlFor={id("position_id")}>
          <Select
            id={id("position_id")}
            style={{ width: 200 }}
            allowClear
            value={v.position_id}
            onChange={(x: number | undefined) => set({ position_id: x })}
            options={positionOptions(positions.data?.items ?? [])}
          />
        </Form.Item>
      </Space>
      <Form.Item label="上長(手動指定)" htmlFor={id("manager_id")}>
        <ManagerSelect
          id={id("manager_id")}
          value={v.manager_id}
          selectedLabel={v.manager_label}
          onChange={(x, label) => set({ manager_id: x, manager_label: label })}
        />
      </Form.Item>
      {/* 電話は任意。電話帳同期(ID管理)が会社電話帳として配る */}
      <Space wrap size="middle" align="start">
        <Form.Item label="会社携帯" htmlFor={id("work_mobile")}>
          <Input id={id("work_mobile")} maxLength={40} placeholder="090-1234-5678" value={v.work_mobile} onChange={(e) => set({ work_mobile: e.target.value })} />
        </Form.Item>
        <Form.Item label="会社電話" htmlFor={id("work_phone")}>
          <Input id={id("work_phone")} maxLength={40} placeholder="03-1234-5678" value={v.work_phone} onChange={(e) => set({ work_phone: e.target.value })} />
        </Form.Item>
        <Form.Item label="内線" htmlFor={id("extension")}>
          <Input id={id("extension")} maxLength={40} placeholder="1234" value={v.extension} onChange={(e) => set({ extension: e.target.value })} />
        </Form.Item>
      </Space>
      <Form.Item label="備考" htmlFor={id("note")}>
        <Input.TextArea id={id("note")} rows={2} maxLength={500} value={v.note} onChange={(e) => set({ note: e.target.value })} />
      </Form.Item>
      {/* カスタム項目(人事 › 設定 › カスタム項目 で定義。会社ごとに違う欄) */}
      {fields.map((f) => (
        <Form.Item key={f.key} label={f.label} htmlFor={id(`extra-${f.key}`)} required={f.required}>
          {f.type === "select" ? (
            <Select
              id={id(`extra-${f.key}`)}
              allowClear
              value={v.extra[f.key] || undefined}
              onChange={(x: string | undefined) => setExtra(f.key, x ?? "")}
              options={f.options.map((o) => ({ value: o, label: o }))}
            />
          ) : f.type === "date" ? (
            <DatePicker
              id={id(`extra-${f.key}`)}
              style={{ width: "100%" }}
              value={v.extra[f.key] ? dayjs(v.extra[f.key]) : null}
              onChange={(d) => setExtra(f.key, d ? d.format("YYYY-MM-DD") : "")}
            />
          ) : (
            <Input id={id(`extra-${f.key}`)} maxLength={200} value={v.extra[f.key] ?? ""} onChange={(e) => setExtra(f.key, e.target.value)} />
          )}
        </Form.Item>
      ))}
    </Form>
  );
}

/** 内容の段。テンプレート・日付・マスタへの反映・返却・SaaS・工程・承認者。備考が要るならテンプレートの入力欄で足す。 */
export function RequestContent({ draft }: { draft: RequestDraft }) {
  const {
    kind,
    templates,
    tpl,
    units,
    positions,
    openLoans,
    activeItemIds,
    templateId,
    setTemplateId,
    startDate,
    setStartDate,
    itemIds,
    setItemIds,
    removeItemIds,
    setRemoveItemIds,
    stepIds,
    setStepIds,
    fields,
    setFields,
    sourceNote,
    orgUnitId,
    setOrgUnitId,
    positionId,
    setPositionId,
    employmentType,
    setEmploymentType,
    returnLoanIds,
    setReturnLoanIds,
    pickerCandidates,
    pickerLoading,
    setPickerSearch,
    approverKind,
    setApproverKind,
    approverId,
    setApproverId,
    approverPreview,
    groupsQ,
    groups: gs,
    dateLabel,
    needsChanges,
  } = draft;
  // 「従業員」型の候補。値は氏名なので同じ氏名は 1 つにまとめる(Select の key が重ならないように)
  const pickerOptions = Array.from(new Map(pickerCandidates.map((e) => [e.name, e])).values()).map((e) => ({
    value: e.name,
    label: `${e.name}(${e.emp_code})`,
  }));
  const preview = approverPreview.data;
  const loginCount = preview?.members.filter((m) => m.has_login).length ?? 0;

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space size="middle" wrap>
        <div>
          <Typography.Text strong>テンプレート</Typography.Text>
          <Select
            style={{ width: 320, display: "block", marginTop: 4 }}
            placeholder="この種別のテンプレート"
            value={templateId}
            loading={templates.isFetching}
            onChange={(v: number) => setTemplateId(v)}
            options={(templates.data?.items ?? []).map((t) => ({
              value: t.id,
              label: `${t.name}${t.is_default ? "(既定)" : ""}`,
            }))}
          />
        </div>
        <div>
          <Typography.Text strong>
            {dateLabel}
            {draft.isNewEmployee && <Typography.Text type="danger"> *</Typography.Text>}
          </Typography.Text>
          <DatePicker
            style={{ display: "block", marginTop: 4 }}
            value={startDate ? dayjs(startDate) : null}
            onChange={(d) => setStartDate(d ? d.format("YYYY-MM-DD") : "")}
          />
          {draft.isNewEmployee && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              新規登録する従業員の入社日にも入る
            </Typography.Text>
          )}
        </div>
      </Space>

      {(kind === "offboard" || kind === "leave") && (
        <Alert
          type="info"
          showIcon
          message="SaaS を1つも選ばなければ、今あるべき SaaS をすべて停止対象にする(選び漏れで残らないように)"
        />
      )}

      {needsChanges && (
        <div>
          <Divider orientation="left" style={{ margin: "4px 0" }}>
            従業員マスタへの反映
          </Divider>
          <Space wrap>
            <div>
              <Typography.Text>{kind === "transfer" ? "異動先の組織(必須)" : "新しい所属"}</Typography.Text>
              <TreeSelect
                style={{ width: 260, display: "block", marginTop: 4 }}
                treeData={unitOptions(units.data?.items ?? [])}
                value={orgUnitId}
                allowClear
                treeDefaultExpandAll
                onChange={(v: number | undefined) => setOrgUnitId(v)}
              />
            </div>
            <div>
              <Typography.Text>役職</Typography.Text>
              <Select
                style={{ width: 200, display: "block", marginTop: 4 }}
                allowClear
                value={positionId}
                onChange={(v: number | undefined) => setPositionId(v)}
                options={positionOptions(positions.data?.items ?? [])}
              />
            </div>
            {kind === "update" && (
              <div>
                <Typography.Text>契約形態</Typography.Text>
                <Select
                  style={{ width: 160, display: "block", marginTop: 4 }}
                  allowClear
                  value={employmentType}
                  onChange={(v: string | undefined) => setEmploymentType(v)}
                  options={EMPLOYMENT_TYPES.map((t) => ({ value: t, label: t }))}
                />
              </div>
            )}
          </Space>
        </div>
      )}

      {kind === "update" && (
        <div>
          <Divider orientation="left" style={{ margin: "4px 0" }}>
            返却する貸与品(個体の名指し)
          </Divider>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            個体を名指しで返す(下の返却工程で品目ごとに返すこともできる。二重指定は不可)
          </Typography.Text>
          <Checkbox.Group
            style={{ display: "block", marginTop: 4 }}
            value={returnLoanIds}
            onChange={(v) => setReturnLoanIds(v as number[])}
          >
            <Space direction="vertical">
              {(openLoans.data?.items ?? []).map((l) => (
                <Checkbox key={l.id} value={l.id} data-return-loan={l.item}>
                  {l.item}
                  {l.asset_no ? (
                    <Typography.Text type="secondary">
                      {" "}
                      {l.asset_no} {l.equipment_name}
                    </Typography.Text>
                  ) : null}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
          {(openLoans.data?.items ?? []).length === 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              貸与中の品目が無い
            </Typography.Text>
          )}
        </div>
      )}

      {kind === "update" && (
        <div>
          <Divider orientation="left" style={{ margin: "4px 0" }}>
            SaaS の無効化
          </Divider>
          <Checkbox.Group
            value={removeItemIds}
            onChange={(v) => setRemoveItemIds(v as number[])}
            options={(tpl.data?.items ?? [])
              .filter((i) => activeItemIds.has(i.item_id))
              .map((i) => ({ value: i.item_id, label: `${i.name}(${itemTag(i)})` }))}
          />
          {activeItemIds.size === 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              今あるべき SaaS が無い
            </Typography.Text>
          )}
        </div>
      )}

      {tpl.isError && (
        <Alert type="error" showIcon message="テンプレートを読めなかった" description={errorText(tpl.error)} />
      )}

      {gs.map((g) => (
        <div key={g.key}>
          <Divider orientation="left" style={{ margin: "4px 0" }}>
            {g.name}
          </Divider>
          <Space direction="vertical" style={{ width: "100%" }}>
            {g.fields.map((f) => (
              // data-field は E2E から入力欄を特定するための目印(section/name)
              <div key={f.id} data-field={fieldKey(f)}>
                <Typography.Text>
                  {f.name}
                  {f.required && <Typography.Text type="danger"> *</Typography.Text>}
                </Typography.Text>
                {f.source.startsWith("emp:") ? (
                  // 従業員マスタの列はサーバーが埋める。入力欄は出さず、入る値を見せるだけ
                  <div style={{ marginTop: 4 }}>
                    <Typography.Text data-fixed={fieldKey(f)}>{draft.fixedText(f)}</Typography.Text>
                  </div>
                ) : f.ftype === "employee" ? (
                  <Select
                    style={{ width: "100%", marginTop: 4 }}
                    allowClear
                    showSearch
                    // サーバーで絞ったものをそのまま出す(手元の 200 件だけで絞らない)
                    filterOption={false}
                    onSearch={setPickerSearch}
                    loading={pickerLoading}
                    placeholder="氏名・社員番号で探す"
                    value={fields[fieldKey(f)] || undefined}
                    onChange={(v: string | undefined) => setFields((p) => ({ ...p, [fieldKey(f)]: v ?? "" }))}
                    options={pickerOptions}
                  />
                ) : f.ftype === "select" ? (
                  <Select
                    style={{ width: "100%", marginTop: 4 }}
                    allowClear
                    value={fields[fieldKey(f)] || undefined}
                    onChange={(v: string) => setFields((p) => ({ ...p, [fieldKey(f)]: v ?? "" }))}
                    options={f.options.map((o) => ({ value: o, label: o }))}
                  />
                ) : f.ftype === "date" ? (
                  <DatePicker
                    style={{ display: "block", marginTop: 4 }}
                    value={fields[fieldKey(f)] ? dayjs(fields[fieldKey(f)]) : null}
                    onChange={(d) =>
                      setFields((p) => ({
                        ...p,
                        [fieldKey(f)]: d ? d.format("YYYY-MM-DD") : "",
                      }))
                    }
                  />
                ) : (
                  <Input
                    style={{ marginTop: 4 }}
                    value={fields[fieldKey(f)] ?? ""}
                    maxLength={500}
                    onChange={(e) => setFields((p) => ({ ...p, [fieldKey(f)]: e.target.value }))}
                  />
                )}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {FIELD_TYPES.find((t) => t.value === f.ftype)?.label}
                  {sourceNote(f) ? ` · ${sourceNote(f)}` : ""}
                </Typography.Text>
              </div>
            ))}

            {g.items.length > 0 && (
              <div>
                <Typography.Text strong style={{ fontSize: 12 }}>
                  SaaS{kind === "update" ? "(有効化)" : ""}
                </Typography.Text>
                <Checkbox.Group
                  style={{ display: "block", marginTop: 4 }}
                  value={itemIds}
                  onChange={(v) =>
                    setItemIds((prev) => replaceWithin(prev, g.items.map((i) => i.item_id), v as number[]))
                  }
                  options={g.items
                    .filter((i) => kind !== "update" || !activeItemIds.has(i.item_id))
                    .map((i) => ({
                      value: i.item_id,
                      label: `${i.name}(${itemTag(i)})${i.required ? " *" : ""}`,
                    }))}
                />
              </div>
            )}

            {g.steps.length > 0 && (
              <div>
                <Typography.Text strong style={{ fontSize: 12 }}>
                  工程
                </Typography.Text>
                <Checkbox.Group
                  style={{ display: "block", marginTop: 4 }}
                  value={stepIds}
                  onChange={(v) =>
                    setStepIds((prev) => replaceWithin(prev, g.steps.map((s) => s.id), v as number[]))
                  }
                >
                  <Space direction="vertical">
                    {g.steps.map((s) => (
                      <Checkbox key={s.id} value={s.id}>
                        {s.name}
                        {s.required && " *"}{" "}
                        <Tag>{s.fulfillment === "external" ? "社外依頼" : "自社で手作業"}</Tag>
                        {s.action === "deprovision" && <Tag color="volcano">回収・停止</Tag>}
                        {s.loan && (
                          <Tooltip title="完了すると貸与品台帳に載る(返却の工程は台帳を閉じる)">
                            <Tag color="blue">貸与</Tag>
                          </Tooltip>
                        )}
                      </Checkbox>
                    ))}
                  </Space>
                </Checkbox.Group>
              </div>
            )}
          </Space>
        </div>
      ))}

      <div data-field="approver">
        <Divider orientation="left" style={{ margin: "4px 0" }}>
          承認者
        </Divider>
        <Space wrap>
          <Select
            style={{ width: 200 }}
            value={approverKind || ""}
            onChange={(v: string) => {
              setApproverKind(v);
              setApproverId(undefined);
            }}
            options={[{ value: "", label: "指定しない(hr:approver なら誰でも)" }, ...APPROVER_KINDS]}
          />
          {approverKind === "employee" && (
            <Select
              style={{ width: 280 }}
              showSearch
              allowClear
              filterOption={false}
              onSearch={setPickerSearch}
              loading={pickerLoading}
              placeholder="氏名・社員番号で探す"
              value={approverId}
              onChange={(v: number | undefined) => setApproverId(v)}
              options={pickerCandidates.map((e) => ({ value: e.id, label: `${e.name}(${e.emp_code})` }))}
            />
          )}
          {approverKind === "unit" && (
            <TreeSelect
              style={{ width: 280 }}
              showSearch
              allowClear
              treeNodeFilterProp="title"
              treeDefaultExpandAll
              placeholder="組織を選ぶ(配下の人も承認者になる)"
              value={approverId}
              onChange={(v: number | undefined) => setApproverId(v)}
              treeData={unitOptions(units.data?.items ?? [])}
            />
          )}
          {approverKind === "group" && (
            <Select
              style={{ width: 280 }}
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder="動的グループを選ぶ"
              value={approverId}
              onChange={(v: number | undefined) => setApproverId(v)}
              options={(groupsQ.data?.items ?? []).map((g) => ({ value: g.id, label: `${g.name}(${g.member_count} 人)` }))}
            />
          )}
        </Space>
        <div style={{ marginTop: 4 }}>
          {approverKind && approverId != null && preview && (
            <Typography.Text type={loginCount ? "secondary" : "warning"} style={{ fontSize: 12 }}>
              「{preview.label}」の {preview.members.length} 人が承認できる(ログインあり {loginCount} 人)
              {preview.members.length > 0 && `: ${preview.members.slice(0, 8).map((m) => m.name).join("、")}${preview.members.length > 8 ? " …" : ""}`}
              {!loginCount && " — まだ誰もログインしていないので、このままでは承認を押せる人がいない"}
            </Typography.Text>
          )}
          {approverKind && approverId != null && approverPreview.isError && (
            <Typography.Text type="danger" style={{ fontSize: 12 }}>
              {errorText(approverPreview.error)}
            </Typography.Text>
          )}
          {!approverKind && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              指定すると、その人(組織なら配下を含む在籍者、動的グループならメンバー)だけが承認・却下できる。承認者には hr:approver の権限も要る
            </Typography.Text>
          )}
        </div>
      </div>
    </Space>
  );
}

/** 確認の段。選んだものを読み上げる(ここでは変えられない。段を戻って直す)。 */
export function RequestConfirm({ draft }: { draft: RequestDraft }) {
  const { kind, tpl, startDate, itemIds, removeItemIds, stepIds, fields, dateLabel } = draft;
  const { orgUnitId, positionId, employmentType, returnLoanIds, units, positions, openLoans } = draft;
  const items = tpl.data?.items ?? [];
  const steps = tpl.data?.steps ?? [];
  const names = (ids: number[], of: { item_id?: number; id?: number; name: string }[], key: "item_id" | "id") =>
    of.filter((x) => ids.includes(x[key] as number)).map((x) => x.name);
  const unitName = units.data?.items.find((u) => u.id === orgUnitId)?.name;
  const positionName = positions.data?.items.find((p) => p.id === positionId)?.name;
  const loans = (openLoans.data?.items ?? []).filter((l) => returnLoanIds.includes(l.id));
  // 退社・休職で SaaS を選ばなかったときは、サーバーが「現在有効な SaaS」(テンプレート指定時は
  // その SaaS に限る)を停止対象にする(wf_service の申請作成)。確認欄を「未入力」にしない(fix1 低3)
  const autoScope =
    (kind === "offboard" || kind === "leave") && itemIds.length === 0
      ? draft.templateId
        ? `テンプレート『${tpl.data?.name ?? "…"}』の SaaS のうち、現在有効なものすべてを停止対象にします`
        : "現在有効な SaaS すべてを停止対象にします"
      : undefined;

  const kv: KvItem[] = [
    { label: "種別", value: `${kindLabel(kind)}申請`, always: true },
    ...targetItems(draft),
    { label: "テンプレート", value: tpl.data?.name, always: true },
    { label: dateLabel, value: startDate, always: true },
    ...(draft.needsChanges
      ? [
          { label: kind === "transfer" ? "異動先の組織" : "新しい所属", value: unitName, always: true },
          { label: "役職", value: positionName },
          ...(kind === "update" ? [{ label: "契約形態", value: employmentType }] : []),
        ]
      : []),
    ...(kind === "update"
      ? [
          {
            label: "返却する貸与品",
            value: loans.length ? loans.map((l) => `${l.item}${l.asset_no ? ` ${l.asset_no}` : ""}`).join(" / ") : undefined,
            wide: true,
          },
          { label: "SaaS の無効化", value: names(removeItemIds, items, "item_id").join(" / ") || undefined, wide: true },
        ]
      : []),
    ...Object.entries(fields)
      .filter(([, v]) => v)
      .map(([k, v]) => ({ label: k, value: v })),
    {
      label: kind === "update" ? "SaaS(有効化)" : "SaaS",
      value: autoScope ?? (names(itemIds, items, "item_id").join(" / ") || undefined),
      wide: true,
      always: true,
    },
    { label: "工程", value: names(stepIds, steps, "id").join(" / ") || undefined, wide: true, always: true },
    {
      label: "承認者",
      value: draft.approverPreview.data
        ? `${draft.approverPreview.data.label}(${draft.approverPreview.data.members.length} 人)`
        : draft.approverKind
          ? "(選択中)"
          : "指定なし(hr:approver なら誰でも)",
      wide: true,
      always: true,
    },
  ];
  return <KeyValueList items={kv} columns={2} unsetLabel="未入力" />;
}
